import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Download, Pause, Play, RotateCcw, RotateCw } from "lucide-react";

/**
 * Waveform player for a single mp3: the file is fetched once, decoded with
 * the Web Audio API for the peaks and played through a plain <audio>
 * element (so playbackRate, background playback and the lock screen
 * controls on phones all keep working). Seek by clicking or dragging on
 * the waveform; space toggles play, arrow keys skip.
 */

export const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;
export type Speed = (typeof SPEEDS)[number];

interface WaveformPlayerProps {
  /** Audio URL (same origin). */
  src: string;
  /** Download link shown next to the controls. */
  downloadHref?: string;
  downloadName?: string;
  /** Called when the file could not be loaded; the HTTP status is passed when known. */
  onLoadError?: (status: number | null) => void;
  /** Called once the audio is decoded and the length is known. */
  onReady?: (seconds: number) => void;
  /** The file is not on the server yet and the page is retrying; shown instead of a hard error. */
  pending?: boolean;
}

const BAR_WIDTH = 3;
const BAR_GAP = 2;
const SKIP_SECONDS = 10;

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Root-mean-square peaks, `count` buckets, normalised to 0..1. */
function computePeaks(buffer: AudioBuffer, count: number): Float32Array {
  const peaks = new Float32Array(count);
  if (count <= 0) return peaks;
  const channels = buffer.numberOfChannels;
  const length = buffer.length;
  const bucket = Math.max(1, Math.floor(length / count));
  for (let ch = 0; ch < channels; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < count; i++) {
      const start = i * bucket;
      const end = Math.min(length, start + bucket);
      let sum = 0;
      for (let j = start; j < end; j++) {
        const v = data[j];
        sum += v * v;
      }
      const rms = Math.sqrt(sum / Math.max(1, end - start));
      if (rms > peaks[i]) peaks[i] = rms;
    }
  }
  let max = 0;
  for (let i = 0; i < count; i++) if (peaks[i] > max) max = peaks[i];
  if (max > 0) for (let i = 0; i < count; i++) peaks[i] = peaks[i] / max;
  return peaks;
}

export function WaveformPlayer({ src, downloadHref, downloadName, onLoadError, onReady, pending }: WaveformPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const decodedRef = useRef<AudioBuffer | null>(null);
  const draggingRef = useRef(false);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [peaks, setPeaks] = useState<Float32Array | null>(null);
  const [width, setWidth] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  // ---- load + decode ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setStatus("loading");
    setPeaks(null);
    decodedRef.current = null;

    (async () => {
      try {
        const res = await fetch(src, { credentials: "include", cache: "no-store" });
        if (!res.ok) {
          if (!cancelled) {
            setStatus("error");
            onLoadError?.(res.status);
          }
          return;
        }
        const bytes = await res.arrayBuffer();
        if (cancelled) return;
        url = URL.createObjectURL(new Blob([bytes], { type: "audio/mpeg" }));
        setBlobUrl(url);

        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (Ctx) {
          const ctx = new Ctx();
          try {
            const decoded = await ctx.decodeAudioData(bytes.slice(0));
            if (cancelled) return;
            decodedRef.current = decoded;
            setDuration(decoded.duration);
            onReady?.(decoded.duration);
          } catch {
            // The <audio> element still plays; we just have no waveform.
          } finally {
            void ctx.close().catch(() => undefined);
          }
        }
        if (!cancelled) setStatus("ready");
      } catch {
        if (!cancelled) {
          setStatus("error");
          onLoadError?.(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // onLoadError/onReady are stable callbacks from the page; re-fetch only when the source changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // ---- size → peaks --------------------------------------------------------------------
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[0]?.contentRect.width ?? 0);
      setWidth(w);
    });
    ro.observe(el);
    setWidth(Math.floor(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const decoded = decodedRef.current;
    if (!decoded || width <= 0) return;
    const bars = Math.max(20, Math.floor(width / (BAR_WIDTH + BAR_GAP)));
    setPeaks(computePeaks(decoded, bars));
  }, [width, status]);

  // ---- draw ----------------------------------------------------------------------------------
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0) return;
    const height = 96;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.floor(width * dpr) || canvas.height !== Math.floor(height * dpr)) {
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const styles = getComputedStyle(canvas);
    const played = styles.getPropertyValue("--vm-wave-played").trim() || "#f7931e";
    const rest = styles.getPropertyValue("--vm-wave-rest").trim() || "#d1d5db";
    const cursor = styles.getPropertyValue("--vm-wave-cursor").trim() || "#111827";

    const progress = duration > 0 ? Math.min(1, current / duration) : 0;
    const mid = height / 2;

    if (!peaks) {
      // Placeholder while decoding: a flat line.
      ctx.fillStyle = rest;
      ctx.fillRect(0, mid - 1, width, 2);
      return;
    }

    const bars = peaks.length;
    const step = width / bars;
    const playedX = progress * width;
    for (let i = 0; i < bars; i++) {
      const x = i * step;
      const h = Math.max(2, peaks[i] * (height - 8));
      ctx.fillStyle = x + BAR_WIDTH / 2 <= playedX ? played : rest;
      const r = Math.min(1.5, BAR_WIDTH / 2);
      const y = mid - h / 2;
      ctx.beginPath();
      ctx.roundRect(x, y, BAR_WIDTH, h, r);
      ctx.fill();
    }
    if (duration > 0) {
      ctx.fillStyle = cursor;
      ctx.fillRect(Math.min(width - 1, playedX), 0, 1.5, height);
    }
  }, [current, duration, peaks, width]);

  useEffect(() => {
    draw();
  }, [draw]);

  // ---- audio element wiring ----------------------------------------------------------
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = speed;
  }, [speed, blobUrl]);

  const tick = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !draggingRef.current) setCurrent(audio.currentTime);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (playing) {
      rafRef.current = requestAnimationFrame(tick);
    } else if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [playing, tick]);

  const onLoadedMetadata = () => {
    const audio = audioRef.current;
    if (audio && Number.isFinite(audio.duration) && audio.duration > 0) {
      setDuration((d) => (d > 0 ? d : audio.duration));
    }
  };

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || status !== "ready") return;
    if (audio.paused) {
      void audio.play().catch(() => setPlaying(false));
    } else {
      audio.pause();
    }
  }, [status]);

  const seekTo = useCallback(
    (seconds: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      const target = Math.max(0, Math.min(duration || audio.duration || 0, seconds));
      audio.currentTime = target;
      setCurrent(target);
    },
    [duration],
  );

  const skip = (delta: number) => seekTo((audioRef.current?.currentTime ?? 0) + delta);

  // ---- pointer seeking on the waveform ---------------------------------------------
  const seekFromPointer = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const target = ratio * duration;
    setCurrent(target);
    return target;
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (status !== "ready" || duration <= 0) return;
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    seekFromPointer(e);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!draggingRef.current) return;
    seekFromPointer(e);
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const target = seekFromPointer(e);
    seekTo(target);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === " " || e.key === "k") {
      e.preventDefault();
      toggle();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      skip(-5);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      skip(5);
    }
  };

  const timeLabel = useMemo(() => `${formatTime(current)} / ${formatTime(duration)}`, [current, duration]);

  return (
    <div className="vm-player" ref={wrapRef} tabIndex={0} onKeyDown={onKeyDown} aria-label="Voicemail player">
      {blobUrl && (
        <audio
          ref={audioRef}
          src={blobUrl}
          preload="auto"
          onLoadedMetadata={onLoadedMetadata}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setCurrent(duration);
          }}
        />
      )}

      <div className={`vm-wave ${status}`}>
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          role="slider"
          aria-label="Position"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(current)}
        />
        {status === "loading" && <div className="vm-wave-overlay">Loading audio…</div>}
        {status === "error" && <div className="vm-wave-overlay">{pending ? "Waiting for the recording…" : "The audio could not be loaded."}</div>}
      </div>

      <div className="vm-controls">
        <div className="vm-transport">
          <button type="button" className="vm-icon-btn" onClick={() => skip(-SKIP_SECONDS)} disabled={status !== "ready"} aria-label={`Back ${SKIP_SECONDS} seconds`} title={`Back ${SKIP_SECONDS} s`}>
            <RotateCcw size={18} />
            <span className="vm-skip-label">{SKIP_SECONDS}</span>
          </button>
          <button type="button" className="vm-play-btn" onClick={toggle} disabled={status !== "ready"} aria-label={playing ? "Pause" : "Play"}>
            {playing ? <Pause size={26} /> : <Play size={26} style={{ marginLeft: 3 }} />}
          </button>
          <button type="button" className="vm-icon-btn" onClick={() => skip(SKIP_SECONDS)} disabled={status !== "ready"} aria-label={`Forward ${SKIP_SECONDS} seconds`} title={`Forward ${SKIP_SECONDS} s`}>
            <RotateCw size={18} />
            <span className="vm-skip-label">{SKIP_SECONDS}</span>
          </button>
          <div className="vm-time" aria-live="off">{timeLabel}</div>
        </div>

        <div className="vm-speeds" role="group" aria-label="Playback speed">
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              className={`vm-speed ${s === speed ? "active" : ""}`}
              onClick={() => setSpeed(s)}
              aria-pressed={s === speed}
            >
              {s}×
            </button>
          ))}
        </div>

        {downloadHref && (
          <a className="vm-download" href={downloadHref} download={downloadName}>
            <Download size={16} /> Download mp3
          </a>
        )}
      </div>
    </div>
  );
}
